import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useAsync } from '../lib/useAsync.ts';
import { useAuth } from '../lib/auth.tsx';
import { formatDate, formatDateTime } from '../lib/format.ts';
import { useCollections, type Section } from '../lib/collections.ts';
import { NewBadge } from './NewBadge.tsx';
import type { Reading, ReadingItemKind, ReadingScope } from '../../../shared/types.ts';

const KIND_LABEL: Record<ReadingItemKind, string> = {
  posicao: 'Posição',
  crescimento: 'Crescimento',
  distancia: 'Distância',
  cadencia: 'Cadência',
  alerta: 'Atenção',
  contexto: 'Contexto',
};

const SOURCE_LABEL: Record<Reading['source'], string> = {
  gerada: 'Gerada pelo painel',
  auditoria: 'Texto da auditoria (preservado)',
  manual: 'Escrita pela equipe',
};

function ReadingBody({ reading }: { reading: Reading }) {
  return (
    <ul className="bullet-list">
      {reading.items.map((item, index) => (
        <li key={index}>
          <span className="badge badge-status-neutral">{KIND_LABEL[item.kind]}</span> {item.text}
        </li>
      ))}
    </ul>
  );
}

/**
 * Leitura executiva de uma conferência. A vigente é a mais recente; as anteriores ficam no arquivo
 * logo abaixo, exatamente como foram geradas — gerar uma nova nunca substitui a antiga.
 */
export function ReadingCard({
  scope,
  title,
  section,
}: {
  scope: ReadingScope;
  title: string;
  section: Section;
}) {
  const { can } = useAuth();
  const collections = useCollections();
  const readings = useAsync(() => api.get<Reading[]>(`/readings?scope=${scope}`), [scope]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function generate() {
    setBusy(true);
    setNotice(null);
    try {
      const before = readings.data?.length ?? 0;
      await api.post('/readings', { scope });
      readings.reload();
      if (before > 0) setNotice('Leitura registrada. Se não houve dado novo desde a última, nada foi acrescentado.');
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Não foi possível gerar a leitura.');
    } finally {
      setBusy(false);
    }
  }

  const all = readings.data ?? [];
  const current = all.find((r) => r.source !== 'auditoria') ?? null;
  const archive = all.filter((r) => r !== current);
  // "Novo" só quando a leitura vigente foi gerada na conferência mais recente (a partir da data da coleta).
  const newest = collections.newest;
  const currentIsNew =
    current !== null &&
    newest !== null &&
    collections.isNew(section) &&
    current.createdAt.slice(0, 10) >= newest.date;

  return (
    <section className={`card${currentIsNew ? ' card--new' : ''}`}>
      <div className="card__header">
        <h2 className="text-title">{title}</h2>
        {currentIsNew && newest && <NewBadge date={newest.date} />}
        {can('superadmin') && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => void generate()} disabled={busy}>
            {busy ? 'Gerando…' : 'Gerar leitura desta conferência'}
          </button>
        )}
      </div>

      {readings.loading && <p className="text-muted text-label">Carregando leitura…</p>}
      {readings.error && <p className="state-error" role="alert">{readings.error}</p>}
      {notice && <p className="callout">{notice}</p>}

      {!readings.loading && !current && (
        <p className="text-muted text-label">
          Nenhuma leitura gerada ainda. Ela é calculada a partir dos números coletados e fica arquivada a cada conferência.
        </p>
      )}

      {current && (
        <>
          <p className="text-body-strong">{current.headline}</p>
          <p className="text-micro text-muted">
            Vigente · {SOURCE_LABEL[current.source]} em {formatDateTime(current.createdAt)} · calculada só com os dados
            da coleta de {formatDate(current.roundId)}.
          </p>
          <ReadingBody reading={current} />
        </>
      )}

      {archive.length > 0 && (
        <details className="reading-archive">
          <summary className="text-label">
            Arquivo: {archive.length} {archive.length === 1 ? 'leitura anterior' : 'leituras anteriores'}
          </summary>
          {archive.map((reading) => (
            <article key={reading.id} className="reading-archive__item">
              <p className="text-body-strong">{reading.headline}</p>
              <p className="text-micro text-muted">
                Coleta de {formatDate(reading.roundId)} · {SOURCE_LABEL[reading.source]}
                {reading.source !== 'auditoria' && ` · registrada em ${formatDateTime(reading.createdAt)}`}
              </p>
              <ReadingBody reading={reading} />
            </article>
          ))}
        </details>
      )}
    </section>
  );
}
